import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import {
  Inbox, ChevronDown, X, Check, Archive, Trash2, Play,
  Eye, EyeOff, Loader2, AlertCircle, Forward, ListTodo,
  Reply, Zap, Bell, RefreshCcw, Activity, FileText,
  ClipboardList, AlertTriangle, MessageCircle, Bot
} from 'lucide-react';
import { toast } from 'sonner';
import DeckTimelineModal from './DeckTimelineModal';

const API = '';

const typeConfig = {
  blocked_action:   { icon: AlertCircle,   color: 'text-amber-500',   bg: 'bg-amber-500/10' },
  action_required:  { icon: Zap,           color: 'text-violet-500',  bg: 'bg-violet-500/10' },
  action_failed:    { icon: AlertTriangle, color: 'text-red-500',     bg: 'bg-red-500/10' },
  forward:          { icon: Forward,       color: 'text-orange-500',  bg: 'bg-orange-500/10' },
  create_task:      { icon: ListTodo,      color: 'text-green-500',   bg: 'bg-green-500/10' },
  reply:            { icon: Reply,         color: 'text-blue-500',    bg: 'bg-blue-500/10' },
  report:           { icon: FileText,      color: 'text-sky-500',     bg: 'bg-sky-500/10' },
  job_output:       { icon: ClipboardList, color: 'text-cyan-500',    bg: 'bg-cyan-500/10' },
  workflow_output:  { icon: Activity,      color: 'text-indigo-500',  bg: 'bg-indigo-500/10' },
  notification:     { icon: Bell,          color: 'text-slate-500',   bg: 'bg-slate-500/10' },
  review_item:      { icon: Bot,           color: 'text-violet-500',  bg: 'bg-violet-500/10' },
  sync_conflict:    { icon: AlertTriangle, color: 'text-amber-600',   bg: 'bg-amber-500/10' },
};

const DeckItem = ({ item, onMarkRead, onArchive, onDelete, onExecute, onRetry, onTimeline }) => {
  const [acting, setActing] = useState(null);
  const config = typeConfig[item.item_type] || typeConfig.action_required;
  const Icon = config.icon;

  const handleAction = async (action, fn) => {
    setActing(action);
    try { await fn(); } 
    catch { toast.error('Action failed'); }
    finally { setActing(null); }
  };

  // Phase 5: a Deck item is retryable if it carries either a capability_id or a
  // linked action_run_id (we'll resolve capability+input from the run).
  const canRetry = Boolean(item.capability_id || item.action_run_id);
  const canTimeline = Boolean(item.action_run_id);

  return (
    <div className={cn(
      "p-3 border-b last:border-b-0 hover:bg-accent/30 transition-colors",
      !item.is_read && "bg-violet-500/5"
    )} data-testid={`deck-item-${item.deck_item_id}`}>
      <div className="flex items-start gap-2.5">
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5", config.bg)}>
          <Icon className={cn("w-4 h-4", config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className={cn("text-sm font-medium truncate", !item.is_read && "font-semibold")}>{item.title}</h4>
            {!item.is_read && <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.summary}</p>

          {/* Phase 5: cross-references chip row */}
          {(item.capability_id || item.source) && (
            <div className="flex flex-wrap items-center gap-1 mt-1.5">
              {item.capability_id && (
                <Badge variant="outline" className="font-mono text-[9px] h-4 px-1.5">
                  {item.capability_id}
                </Badge>
              )}
              {item.source && (
                <Badge variant="secondary" className="text-[9px] h-4 px-1.5">{item.source}</Badge>
              )}
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {item.content?.action && (
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 gap-1"
                disabled={acting === 'execute'}
                onClick={() => handleAction('execute', () => onExecute(item.deck_item_id))}
                data-testid={`deck-execute-${item.deck_item_id}`}>
                {acting === 'execute' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Execute
              </Button>
            )}
            {canRetry && (
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 gap-1"
                disabled={acting === 'retry'}
                onClick={() => handleAction('retry', () => onRetry(item))}
                data-testid={`deck-retry-${item.deck_item_id}`}>
                {acting === 'retry' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                Retry
              </Button>
            )}
            {canTimeline && (
              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 gap-1"
                onClick={() => onTimeline(item)}
                data-testid={`deck-timeline-${item.deck_item_id}`}>
                <Activity className="w-3 h-3" />
                Timeline
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 gap-1"
              onClick={() => handleAction('read', () => item.is_read ? onMarkRead(item.deck_item_id, true) : onMarkRead(item.deck_item_id, false))}
              data-testid={`deck-read-${item.deck_item_id}`}>
              {item.is_read ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {item.is_read ? 'Unread' : 'Read'}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 gap-1"
              onClick={() => handleAction('archive', () => onArchive(item.deck_item_id))}
              data-testid={`deck-archive-${item.deck_item_id}`}>
              <Archive className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 gap-1 text-red-500 hover:text-red-600"
              onClick={() => handleAction('delete', () => onDelete(item.deck_item_id))}
              data-testid={`deck-delete-${item.deck_item_id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5 pl-10">
        {new Date(item.created_at || item.task_created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

export default function DeckWidget({ collapsed: sidebarCollapsed }) {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [timelineItem, setTimelineItem] = useState(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ai/deck/unread-count`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch {}
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ai/deck?limit=30`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setItems(data);
      }
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Poll unread count
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch items when opened
  useEffect(() => {
    if (isOpen) fetchItems();
  }, [isOpen, fetchItems]);

  const markRead = async (id, markUnread = false) => {
    const endpoint = markUnread ? `${API}/api/ai/deck/${id}/unread` : `${API}/api/ai/deck/${id}/read`;
    await fetch(endpoint, { method: 'POST', credentials: 'include' });
    setItems(prev => prev.map(i => i.deck_item_id === id ? { ...i, is_read: !markUnread } : i));
    fetchUnreadCount();
  };

  const archiveItem = async (id) => {
    await fetch(`${API}/api/ai/deck/${id}/archive`, { method: 'POST', credentials: 'include' });
    setItems(prev => prev.filter(i => i.deck_item_id !== id));
    fetchUnreadCount();
    toast.success('Archived');
  };

  const deleteItem = async (id) => {
    await fetch(`${API}/api/ai/deck/${id}`, { method: 'DELETE', credentials: 'include' });
    setItems(prev => prev.filter(i => i.deck_item_id !== id));
    fetchUnreadCount();
    toast.success('Deleted');
  };

  const executeItem = async (id) => {
    const res = await fetch(`${API}/api/ai/deck/${id}/execute`, {
      method: 'POST', credentials: 'include'
    });
    if (res.ok) {
      const result = await res.json();
      if (result.status === 'completed' || result.status === 'ready') {
        toast.success('Executed successfully');
        setItems(prev => prev.filter(i => i.deck_item_id !== id));
      } else {
        toast.info(result.message || 'Action processed');
      }
      fetchUnreadCount();
    }
  };

  // Phase 5 — retry the action behind a Deck item via TEG v2.
  const retryItem = async (item) => {
    const id = item.deck_item_id;
    const res = await fetch(`${API}/api/ai/v2/deck/${id}/retry`, {
      method: 'POST', credentials: 'include'
    });
    if (!res.ok) {
      let body; try { body = await res.json(); } catch { /* ignore */ }
      const msg = body?.error?.message || body?.detail?.message || body?.detail || `Retry failed (HTTP ${res.status})`;
      toast.error(msg);
      return;
    }
    const result = await res.json();
    if (result.success) {
      toast.success('Retried — action executed', {
        description: result.action_run_id ? `Run ${result.action_run_id}` : undefined,
      });
      setItems(prev => prev.filter(i => i.deck_item_id !== id));
    } else {
      const ec = result?.error?.error_code || 'AI-TOOL';
      const m = result?.error?.message || result?.error?.title || 'Retry failed';
      toast.error(`${ec}: ${m}`);
    }
    fetchUnreadCount();
  };

  return (
    <>
      {/* Deck trigger button - positioned between logo and nav */}
      <button 
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 rounded-lg orka-transition-colors hover:bg-accent/50 relative",
          sidebarCollapsed && "justify-center px-2",
          isOpen && "bg-accent"
        )}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="deck-widget-trigger"
      >
        <div className="relative">
          <Inbox className={cn("w-5 h-5", unreadCount > 0 ? "text-violet-500" : "text-muted-foreground")} />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center animate-in zoom-in">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        {!sidebarCollapsed && (
          <>
            <span className="text-sm font-medium flex-1 text-left">The Deck</span>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] bg-violet-500/10 text-violet-600">
                {unreadCount} new
              </Badge>
            )}
          </>
        )}
      </button>

      {/* Slide-out panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          
          {/* Panel */}
          <div 
            className={cn(
              "fixed top-16 z-50 w-80 bg-card border rounded-xl shadow-2xl shadow-black/10 overflow-hidden",
              "animate-in slide-in-from-left-2 fade-in duration-200",
              sidebarCollapsed ? "left-16 ml-2" : "left-56 ml-2"
            )}
            style={{ maxHeight: 'calc(100vh - 5rem)' }}
            data-testid="deck-panel"
          >
            {/* Panel header */}
            <div className="p-3 border-b flex items-center justify-between bg-gradient-to-r from-violet-500/5 to-transparent">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-violet-500" />
                <h3 className="text-sm font-semibold">The Deck</h3>
                {items.length > 0 && (
                  <Badge variant="outline" className="text-[10px] h-4">{items.length}</Badge>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsOpen(false)}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Items list */}
            <ScrollArea style={{ maxHeight: 'calc(100vh - 10rem)' }}>
              {loading ? (
                <div className="p-6 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : items.length === 0 ? (
                <div className="p-6 text-center">
                  <Inbox className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">All clear!</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Actions that need your attention will appear here</p>
                </div>
              ) : (
                items.map(item => (
                  <DeckItem key={item.deck_item_id} item={item}
                    onMarkRead={markRead} onArchive={archiveItem}
                    onDelete={deleteItem} onExecute={executeItem}
                    onRetry={retryItem}
                    onTimeline={(it) => setTimelineItem(it)}
                  />
                ))
              )}
            </ScrollArea>
          </div>
        </>
      )}

      {/* Phase 5 — Action timeline modal */}
      <DeckTimelineModal
        open={Boolean(timelineItem)}
        onClose={() => setTimelineItem(null)}
        actionRunId={timelineItem?.action_run_id}
        deckItem={timelineItem}
      />
    </>
  );
}
